import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  MessageSquare, Heart, Smile, Meh, Frown, Loader2, CheckCircle2,
  Trash2, ExternalLink, RefreshCw, Filter, User as UserIcon,
} from "lucide-react";
import { Button } from "./ui/button";
import {
  fetchAdminFeedback, updateAdminFeedback, deleteAdminFeedback, fetchUsers,
} from "../lib/api";
import { toast } from "sonner";

const RATING_META = {
  great: { label: "Great",     Icon: Heart, cls: "text-emerald-300 border-emerald-500/40 bg-emerald-500/10" },
  good:  { label: "Good",      Icon: Smile, cls: "text-sky-300     border-sky-500/40     bg-sky-500/10" },
  okay:  { label: "Okay",      Icon: Meh,   cls: "text-amber-300   border-amber-500/40   bg-amber-500/10" },
  bad:   { label: "Not great", Icon: Frown, cls: "text-rose-300    border-rose-500/40    bg-rose-500/10" },
};

function relativeTime(iso) {
  if (!iso) return "";
  const then = new Date(iso);
  const diff = (Date.now() - then.getTime()) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 86400 * 7) return `${Math.floor(diff / 86400)}d ago`;
  return then.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

/**
 * Admin-only feedback inbox. Shows all submitted anonymous feedback,
 * newest first. Admin can mark reviewed, see auto-tagged team members
 * named in the text, and delete obvious spam.
 */
export function FeedbackInbox() {
  const [items, setItems] = useState(null);
  const [users, setUsers] = useState([]);
  const [filter, setFilter] = useState("unreviewed"); // unreviewed | all
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [list, roster] = await Promise.all([
        fetchAdminFeedback({ unreviewed_only: filter === "unreviewed" }),
        fetchUsers().catch(() => []),
      ]);
      setItems(list);
      setUsers(roster || []);
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, [filter]);

  const userById = useMemo(() => {
    const m = {};
    for (const u of users) m[u.user_id] = u;
    return m;
  }, [users]);

  const markReviewed = async (id, reviewed) => {
    try {
      await updateAdminFeedback(id, { reviewed });
      setItems((cur) =>
        (cur || []).map((f) => (f.id === id ? { ...f, reviewed } : f))
      );
      toast.success(reviewed ? "Marked reviewed" : "Reopened");
    } catch {
      toast.error("Could not update");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this feedback entry? This cannot be undone.")) return;
    try {
      await deleteAdminFeedback(id);
      setItems((cur) => (cur || []).filter((f) => f.id !== id));
      toast.success("Deleted");
    } catch {
      toast.error("Could not delete");
    }
  };

  return (
    <section data-testid="feedback-inbox">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
        <div className="flex items-center gap-2">
          <MessageSquare className="w-4 h-4 text-emerald-300" />
          <h3 className="text-sm font-semibold uppercase tracking-[0.12em] font-mono text-emerald-300">
            Feedback inbox
          </h3>
          {items && (
            <span className="text-[10px] uppercase tracking-wider font-mono px-1.5 py-0.5 rounded bg-emerald-500/15 text-emerald-200 border border-emerald-500/30">
              {items.length}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <div className="inline-flex items-center gap-1 text-xs">
            <Filter className="w-3 h-3 text-zinc-500" />
            <button
              data-testid="feedback-filter-unreviewed"
              type="button"
              onClick={() => setFilter("unreviewed")}
              className={`px-2 py-1 rounded text-xs font-mono ${
                filter === "unreviewed"
                  ? "bg-emerald-500/15 text-emerald-200 border border-emerald-500/30"
                  : "text-zinc-400 hover:text-zinc-200"
              }`}
            >
              Unreviewed
            </button>
            <button
              data-testid="feedback-filter-all"
              type="button"
              onClick={() => setFilter("all")}
              className={`px-2 py-1 rounded text-xs font-mono ${
                filter === "all"
                  ? "bg-emerald-500/15 text-emerald-200 border border-emerald-500/30"
                  : "text-zinc-400 hover:text-zinc-200"
              }`}
            >
              All
            </button>
          </div>
          <Button
            size="sm"
            variant="outline"
            onClick={load}
            disabled={loading}
            data-testid="feedback-refresh"
            className="border-zinc-800 bg-zinc-950 hover:bg-zinc-900 text-xs h-8"
          >
            <RefreshCw className={`w-3 h-3 mr-1 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </Button>
        </div>
      </div>

      {items === null ? (
        <div className="panel p-6 flex items-center justify-center">
          <Loader2 className="w-5 h-5 animate-spin text-emerald-400" />
        </div>
      ) : items.length === 0 ? (
        <div className="panel p-6 text-center text-xs text-zinc-500 italic">
          {filter === "unreviewed"
            ? "Inbox zero — no unreviewed feedback."
            : "No feedback submitted yet."}
        </div>
      ) : (
        <ul className="space-y-2">
          {items.map((f) => {
            const meta = f.rating ? RATING_META[f.rating] : null;
            const tagged = (f.tagged_user_ids || [])
              .map((id) => userById[id])
              .filter(Boolean);
            return (
              <li
                key={f.id}
                data-testid={`feedback-entry-${f.id}`}
                className={`panel p-3.5 space-y-2 ${
                  f.reviewed ? "opacity-60" : ""
                }`}
              >
                <div className="flex items-start gap-3 flex-wrap">
                  {meta && (
                    <span
                      className={`shrink-0 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border ${meta.cls}`}
                    >
                      <meta.Icon className="w-3 h-3" />
                      {meta.label}
                    </span>
                  )}
                  <div className="flex-1 min-w-0">
                    <Link
                      to={`/dispensaries/${f.dispensary_id}`}
                      className="text-sm font-medium text-zinc-100 hover:text-emerald-300 inline-flex items-center gap-1 truncate"
                    >
                      {f.dispensary_name || "Unknown dispensary"}
                      <ExternalLink className="w-3 h-3 text-zinc-600" />
                    </Link>
                    <div className="text-[11px] text-zinc-500 font-mono">
                      {f.tasting_date && `${f.tasting_date} · `}
                      submitted {relativeTime(f.submitted_at)}
                    </div>
                  </div>
                  <div className="shrink-0 inline-flex items-center gap-1">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => markReviewed(f.id, !f.reviewed)}
                      data-testid={`feedback-toggle-reviewed-${f.id}`}
                      className="border-zinc-800 bg-zinc-950 hover:bg-zinc-900 text-xs h-7"
                    >
                      <CheckCircle2 className={`w-3 h-3 mr-1 ${f.reviewed ? "text-emerald-400" : ""}`} />
                      {f.reviewed ? "Reviewed" : "Mark reviewed"}
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => remove(f.id)}
                      data-testid={`feedback-delete-${f.id}`}
                      className="border-zinc-800 bg-zinc-950 hover:bg-zinc-900 hover:text-rose-300 text-xs h-7 w-7 p-0"
                      title="Delete"
                    >
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </div>
                </div>

                {f.shoutouts && (
                  <div className="text-sm text-zinc-200 leading-snug whitespace-pre-wrap">
                    <span className="text-[10px] uppercase tracking-wider font-mono text-zinc-500 mr-1.5">
                      Shoutout:
                    </span>
                    {f.shoutouts}
                  </div>
                )}
                {f.feedback && (
                  <div className="text-sm text-zinc-200 leading-snug whitespace-pre-wrap">
                    <span className="text-[10px] uppercase tracking-wider font-mono text-zinc-500 mr-1.5">
                      Feedback:
                    </span>
                    {f.feedback}
                  </div>
                )}

                {tagged.length > 0 && (
                  <div className="flex items-center gap-1.5 flex-wrap pt-1 border-t border-zinc-800/60">
                    <span className="text-[10px] uppercase tracking-wider font-mono text-zinc-500">
                      Tagged:
                    </span>
                    {tagged.map((u) => (
                      <span
                        key={u.user_id}
                        data-testid={`feedback-tag-${f.id}-${u.user_id}`}
                        className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-mono uppercase tracking-wider border border-indigo-500/30 bg-indigo-500/10 text-indigo-200"
                      >
                        <UserIcon className="w-2.5 h-2.5" />
                        {u.name || u.email}
                      </span>
                    ))}
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}

export default FeedbackInbox;
